import { FC, useCallback } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { AppointmentSelectorComponents } from '@frontend/appointment-selector';
import { EditableText } from '@frontend/editable-text';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, BannerNotification, ContentLoader, Text, useForm, BaseFieldProps } from '@frontend/design-system';
import { useSendFormsStore } from '../../../providers';
import {
  accordionBodyStyle,
  accordionHeaderStyle,
  accordionItemStyle,
  appointmentsContainerStyle,
  bannerNotificationStyle,
  textStyle,
  tileHeaderContentStyle,
  titleStyle,
} from './appointment-selection.styles';
import AttachDocuments from './attach-documents/attach-documents.component';

const EMPTY_APPOINTMENTS: Appointment[] = [];

const Appointments: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { personId, locationId, setAppointmentId, setAppointmentInfo } = useSendFormsStore([
    'personId',
    'locationId',
    'setAppointmentInfo',
    'setAppointmentId',
  ]);
  const appointmentsQuery = AppointmentsApiQueries.useListPersonAppointments({
    locationId,
    personId,
  });
  const appointments = appointmentsQuery.data?.appointments ?? EMPTY_APPOINTMENTS;

  const selectAppointmentHandler = useCallback(
    (appointment: Appointment) => {
      setAppointmentInfo({
        appointmentAt: appointment.appointmentAt,
        pmsType: appointment.pmsType,
        practitioner: appointment.practitioner,
      });
      setAppointmentId(appointment?.appointmentId);
    },
    [setAppointmentInfo, setAppointmentId]
  );

  return (
    <>
      {appointments.length > 0 ? (
        <div css={appointmentsContainerStyle}>
          <Text color='light' css={textStyle}>
            {t("Link a form to recipient's upcoming appointment")}
          </Text>
          <AppointmentSelectorComponents.AppointmentsList
            appointments={appointments}
            onSelect={selectAppointmentHandler}
          />
        </div>
      ) : (
        <BannerNotification status='info' css={bannerNotificationStyle}>
          {t('No Upcoming Appointments')}
        </BannerNotification>
      )}
      <ContentLoader show={appointmentsQuery.isLoading} message={t('Loading appointments...')} />
    </>
  );
};

/**
 * Enhanced "Add More Details" step — renders appointment linking, an internal
 * note, and document attachment inside collapsible accordion sections.
 *
 * This is the NEW flow gated behind the `send-forms-enhanced-flow` wetag.
 * When the feature reaches 100% rollout, this replaces the original
 * `AppointmentSelectionStep`.
 */
const InternalNote: FC = () => {
  const { getFieldProps } = useForm({
    fields: {
      internalNote: { type: 'text', value: '' },
    },
  });

  return <EditableText fieldProps={getFieldProps('internalNote')} />;
};

type TileHeaderProps = {
  iconName: 'link' | 'note' | 'attachment';
  title: string;
  subTitle?: string;
  editableTitleProps?: BaseFieldProps;
};

const TileHeader: FC<TileHeaderProps> = ({ iconName, title, subTitle }) => {
  return (
    <div css={tileHeaderContentStyle}>
      <Icon name={iconName} size={20} />
      <div css={titleStyle}>
        <Text css={{ fontSize: theme.fontSize(18), lineHeight: 1.3, textAlign: 'left' }} weight='bold'>
          {title}
        </Text>

        {subTitle && (
          <Text color='subdued' css={{ textAlign: 'left' }}>
            {subTitle}
          </Text>
        )}
      </div>
    </div>
  );
};

const AddMoreDetailsStep: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');

  return (
    <Accordion
      shouldRenderBody
      css={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Accordion.Item value='link-appointments' css={accordionItemStyle}>
        <Accordion.Header css={accordionHeaderStyle}>
          <TileHeader title={t('Link Appointments')} iconName='link' subTitle={t('Link appointments with a form')} />
        </Accordion.Header>
        <Accordion.Body css={accordionBodyStyle}>
          <Appointments />
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item value='add-internal-note' css={accordionItemStyle}>
        <Accordion.Header css={accordionHeaderStyle}>
          <TileHeader
            title={t('Add Office Use Only Note')}
            iconName='note'
            subTitle={t('Pre-fill details for this particular patient on this form')}
          />
        </Accordion.Header>
        <Accordion.Body css={accordionBodyStyle}>
          <InternalNote />
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item value='add-attachment' css={accordionItemStyle}>
        <Accordion.Header css={accordionHeaderStyle}>
          <TileHeader title={t('Add Attachment')} iconName='attachment' />
        </Accordion.Header>
        <Accordion.Body css={accordionBodyStyle}>
          <AttachDocuments />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default AddMoreDetailsStep;
